import { useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Copy, Check, Send } from "lucide-react";

const ShareToolkitDialog = ({ open, onOpenChange, toolkitId, toolkitTitle }) => {
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setEmail("");
    setNote("");
    setShareUrl(null);
    setCopied(false);
  };

  const create = async () => {
    setLoading(true);
    try {
      const payload = { toolkit_id: toolkitId };
      if (note.trim()) payload.note = note.trim();
      if (email.trim()) payload.recipient_email = email.trim();
      const { data } = await api.post(`/toolkit/${toolkitId}/share`, payload);
      const abs = data.share_url.startsWith("http")
        ? data.share_url
        : `${window.location.origin}${data.share_url}`;
      setShareUrl(abs);
      if (email.trim()) toast.success("Sent — link is in their inbox.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create share link");
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent
        className="bg-[#0c1510] border border-teal-500/25 text-neutral-100 max-w-lg"
        data-testid="share-dialog"
      >
        <DialogHeader>
          <div className="text-xs uppercase tracking-[0.24em] text-teal-300/80 mb-1">Therapist Share</div>
          <DialogTitle className="font-display text-2xl">Share "{toolkitTitle}"</DialogTitle>
          <DialogDescription className="text-neutral-400">
            Creates a one-time secure link with your reflections. Opens once, then closes. Expires in 7 days.
          </DialogDescription>
        </DialogHeader>

        {!shareUrl ? (
          <div className="space-y-4 py-2">
            <label className="block">
              <span className="text-xs text-neutral-400 uppercase tracking-wider">Send directly to (optional)</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="therapist@example.com"
                data-testid="share-email-input"
                className="wwr-input w-full rounded-xl px-4 py-2.5 mt-1 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs text-neutral-400 uppercase tracking-wider">A note (optional)</span>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Anything you want them to know before they open it."
                rows={3}
                data-testid="share-note-input"
                className="wwr-input w-full rounded-xl px-4 py-2.5 mt-1 text-sm resize-none"
              />
            </label>
          </div>
        ) : (
          <div className="space-y-4 py-2" data-testid="share-created">
            <p className="text-sm text-neutral-300">Your one-time link is ready:</p>
            <div className="flex gap-2">
              <input
                readOnly
                value={shareUrl}
                data-testid="share-url"
                className="wwr-input flex-1 rounded-xl px-3 py-2 text-xs"
              />
              <button
                onClick={copy}
                data-testid="share-copy-btn"
                className="btn-ghost px-3 py-2 text-xs inline-flex items-center gap-1"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <p className="text-xs text-neutral-500">
              This link opens once, then closes. Anyone with the link can view — please share only with someone you trust.
            </p>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          {!shareUrl ? (
            <button
              onClick={create}
              disabled={loading}
              data-testid="share-create-btn"
              className="btn-primary px-5 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-60"
            >
              <Send size={14} /> {loading ? "Creating…" : email.trim() ? "Send & get link" : "Create link"}
            </button>
          ) : (
            <button
              onClick={() => onOpenChange(false)}
              data-testid="share-done-btn"
              className="btn-primary px-5 py-2 text-sm"
            >
              Done
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ShareToolkitDialog;
